/**
 * Khai báo khoá query + các hook đọc dữ liệu dùng chung, và CẦU NỐI realtime → TanStack Query.
 *
 * Mọi màn hình đọc dữ liệu qua đây để khoá query thống nhất; nhờ vậy khi WebSocket
 * báo "tủ vừa đổi trạng thái", chỉ cần huỷ hiệu lực đúng khoá là toàn bộ màn hình
 * liên quan tự tải lại — không màn nào phải tự viết logic đồng bộ riêng.
 */

import { EVENT_LENH_ACK, EVENT_CANH_BAO_MOI, EVENT_CANH_BAO_DONG } from './api';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { realtime, type RtMessage, type RtStatus } from './ws';
import { taoGa } from './ga';
import {
  nhanKhungNguonGiu,
  ngheNguonGiuSong, phienBanNguonGiuSong, docNguonGiuSong,
} from './nguongiulive';
import type {
  Alarm, AuditRow, CabinetDetail, Command, DeviceLogRow, MeterSample, Overview,
  ProjectRow, RouteRow, ScheduleHistoryRow, ScheduleResponse, Strategy, TrangThaiMoPhong,
  TreeRoute, TuBiChanRow, TuChoDuyet, TuChoXuLy, UserRow, ThongKeFirmware,
} from './types';
import type { KetQuaGom } from './bieudo';

/* ------------------------------------------------------------ khoá query */

export const qk = {
  overview: ['overview'] as const,
  tree: ['tree'] as const,
  routes: ['routes'] as const,
  projects: ['projects'] as const,
  cabinet: (id: string) => ['cabinet', id] as const,
  meter: (id: string, hours: number) => ['meter', id, hours] as const,
  meterLatest: (id: string) => ['meter-latest', id] as const,
  /** TK-16 B4: số đo TRỰC TIẾP từ khung `cabinet.meter` — chỉ ghi bằng setQueryData, KHÔNG BAO GIỜ gọi API. */
  meterTrucTiep: (id: string) => ['meter-truc-tiep', id] as const,
  commands: (id: string) => ['commands', id] as const,
  logs: (id: string) => ['device-logs', id] as const,
  alarms: (cabinetId?: string) => ['alarms', cabinetId ?? 'all'] as const,
  schedule: (id: string) => ['schedule', id] as const,
  scheduleHistory: (id: string) => ['schedule-history', id] as const,
  strategies: ['strategies'] as const,
  users: ['users'] as const,
  danhSachCho: ['danh-sach-cho'] as const,
  /** TK-17 */
  choDuyet: ['cho-duyet'] as const,
  biChan: ['bi-chan'] as const,
  audit: (action: string) => ['audit', action] as const,
  moPhong: ['mo-phong'] as const,
};

/* -------------------------------------------------------------- hook đọc */

/**
 * Chỉ mở ra vài tuỳ chọn thật sự dùng tới — cố tình KHÔNG nhận nguyên gói
 * UseQueryOptions để không ai lỡ tay ghi đè queryKey/queryFn từ chỗ gọi.
 */
interface Opts<T> {
  enabled?: boolean;
  refetchInterval?: number | false;
  staleTime?: number;
  /** giữ tham số kiểu để chỗ gọi vẫn suy ra đúng T */
  initialData?: T;
}

export function useOverview(opts?: Opts<Overview>) {
  return useQuery({
    queryKey: qk.overview,
    queryFn: () => api.get<Overview>('/api/overview'),
    refetchInterval: 30_000,
    ...opts,
  });
}

export function useTree(opts?: Opts<TreeRoute[]>) {
  return useQuery({
    queryKey: qk.tree,
    queryFn: () => api.get<TreeRoute[]>('/api/tree'),
    refetchInterval: 60_000,
    ...opts,
  });
}

export function useRoutes(opts?: Opts<RouteRow[]>) {
  return useQuery({
    queryKey: qk.routes,
    queryFn: () => api.get<RouteRow[]>('/api/routes'),
    ...opts,
  });
}

/**
 * DANH SÁCH CHỜ — tủ thiếu tên, thiếu đường, hoặc cả hai.
 *
 * Không có truy vấn này thì tủ vừa cắm là **vô hình**: cây tài sản duyệt theo tuyến chiếu sáng,
 * nên cái gì không có đường thì không có chỗ nào để hiện ra.
 *
 * ⚠ Tên hàm, tên khoá và đường dẫn đổi CÙNG LÚC với nhãn trên màn hình (YC-24 mục B).
 *   Để lại một cái tên cũ ở bất kỳ tầng nào là để lại đúng chỗ mà nhãn và dữ liệu trôi khỏi nhau.
 */
export function useDanhSachCho(opts?: Opts<TuChoXuLy[]>) {
  return useQuery({
    queryKey: qk.danhSachCho,
    queryFn: () => api.get<TuChoXuLy[]>('/api/cabinets/danh-sach-cho'),
    ...opts,
  });
}

/** TK-17 — tủ vừa REGISTER lần đầu, chưa ai duyệt. Chỉ `SUPER_ADMIN`. */
export function useChoDuyet(opts?: Opts<TuChoDuyet[]>) {
  return useQuery({
    queryKey: qk.choDuyet,
    queryFn: () => api.get<TuChoDuyet[]>('/api/cabinets/cho-duyet'),
    ...opts,
  });
}

/** TK-17 — bảng chặn theo MAC. Chỉ `SUPER_ADMIN`. */
export function useBiChan(opts?: Opts<TuBiChanRow[]>) {
  return useQuery({
    queryKey: qk.biChan,
    queryFn: () => api.get<TuBiChanRow[]>('/api/cabinets/bi-chan'),
    ...opts,
  });
}

/** TK-20 — MỘT mẫu đo MỚI NHẤT của tủ (`/meter/latest`). Ô số ở Tổng quan đọc ở đây, không đọc lịch sử. */
export function useMeterLatest(id: string | undefined, opts?: Opts<MeterSample | null>) {
  return useQuery({
    queryKey: qk.meterLatest(id ?? ''),
    queryFn: () => api.get<MeterSample | null>(`/api/cabinets/${id}/meter/latest`),
    enabled: !!id,
    ...opts,
  });
}

export function useProjects(opts?: Opts<ProjectRow[]>) {
  return useQuery({
    queryKey: qk.projects,
    queryFn: () => api.get<ProjectRow[]>('/api/projects'),
    ...opts,
  });
}

export function useCabinet(id: string | undefined, opts?: Opts<CabinetDetail>) {
  return useQuery({
    queryKey: qk.cabinet(id ?? ''),
    queryFn: () => api.get<CabinetDetail>(`/api/cabinets/${id}`),
    enabled: !!id,
    ...opts,
  });
}

/**
 * TK-20 — lịch sử số đo GOM THEO Ô (`/meter/o`): cửa sổ `hours` chia số ô cố định, mỗi ô avg/min/max.
 * Bản trước gọi `/meter?take=800` ⇒ nhận 800 dòng CŨ NHẤT của cửa sổ và vẽ như thể là hiện tại.
 * Khoá giữ tiền tố `['meter', id]` — đường huỷ bộ đệm bên dưới không phải đổi.
 */
export function useMeterHistory(id: string | undefined, hours = 24, opts?: Opts<KetQuaGom>) {
  return useQuery({
    queryKey: qk.meter(id ?? '', hours),
    queryFn: () => api.get<KetQuaGom>(`/api/cabinets/${id}/meter/o?hours=${hours}`),
    enabled: !!id,
    ...opts,
  });
}

export function useCommands(id: string | undefined, take = 50, opts?: Opts<Command[]>) {
  return useQuery({
    queryKey: qk.commands(id ?? ''),
    queryFn: () => api.get<Command[]>(`/api/cabinets/${id}/commands?take=${take}`),
    enabled: !!id,
    ...opts,
  });
}

export function useDeviceLogs(id: string | undefined, take = 100, opts?: Opts<DeviceLogRow[]>) {
  return useQuery({
    queryKey: qk.logs(id ?? ''),
    queryFn: () => api.get<DeviceLogRow[]>(`/api/cabinets/${id}/logs?take=${take}`),
    enabled: !!id,
    ...opts,
  });
}

/** TK-25: hồ sơ ĐÃ ĐÓNG trong 7 ngày (người bấm hoặc máy chủ tự đóng) — ô "Đã xử lý · 7 ngày qua". */
export function useAlarmsDaDong(ngay = 7, opts?: Opts<Alarm[]>) {
  return useQuery({
    queryKey: [...qk.alarms(), 'da-dong', ngay] as const,
    queryFn: () => api.get<Alarm[]>(`/api/alarms?active=false&daDongNgay=${ngay}&take=50`),
    refetchInterval: 120_000,
    ...opts,
  });
}

export function useAlarms(cabinetId?: string, opts?: Opts<Alarm[]>) {
  return useQuery({
    queryKey: qk.alarms(cabinetId),
    queryFn: () => api.get<Alarm[]>(
      `/api/alarms?active=true&take=100${cabinetId ? `&cabinetId=${cabinetId}` : ''}`
    ),
    refetchInterval: 60_000,
    ...opts,
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * CÓ ĐANG MÔ PHỎNG NGÕ VÀO KHÔNG — nuôi dải báo thường trực ở `Layout.tsx`.
 *
 * ⛔ KHÔNG có `enabled` theo vai. Tuyến `GET /api/mo-phong/trang-thai` cố ý không đòi ADMIN,
 *   và hook cũng vậy: người bị số liệu giả đánh lừa là NGƯỜI TRỰC, không phải người bật nó.
 *   Một dải cảnh báo chỉ quản trị viên thấy thì đúng bằng không có dải.
 *
 * ⚠ 60 giây, không phải một lần rồi thôi: công tắc nằm ở biến môi trường máy chủ, nên nó có
 *   thể đổi khi máy chủ khởi động lại giữa lúc người trực đang mở tab. `staleTime` để 0 để
 *   lần đổi tab cũng kéo lại — trả lời sai ở đây là dải biến mất trong khi mô phỏng vẫn chạy.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function useMoPhong(opts?: Opts<TrangThaiMoPhong>) {
  return useQuery({
    queryKey: qk.moPhong,
    queryFn: () => api.get<TrangThaiMoPhong>('/api/mo-phong/trang-thai'),
    refetchInterval: 60_000,
    ...opts,
  });
}

export function useSchedule(id: string | undefined, opts?: Opts<ScheduleResponse>) {
  return useQuery({
    queryKey: qk.schedule(id ?? ''),
    queryFn: () => api.get<ScheduleResponse>(`/api/cabinets/${id}/schedule`),
    enabled: !!id,
    ...opts,
  });
}

export function useScheduleHistory(id: string | undefined, opts?: Opts<ScheduleHistoryRow[]>) {
  return useQuery({
    queryKey: qk.scheduleHistory(id ?? ''),
    queryFn: () => api.get<ScheduleHistoryRow[]>(`/api/cabinets/${id}/schedule/history`),
    enabled: !!id,
    ...opts,
  });
}

export function useStrategies(opts?: Opts<Strategy[]>) {
  return useQuery({
    queryKey: qk.strategies,
    queryFn: () => api.get<Strategy[]>('/api/strategies'),
    ...opts,
  });
}

export function useUsers(enabled: boolean, opts?: Opts<UserRow[]>) {
  return useQuery({
    queryKey: qk.users,
    queryFn: () => api.get<UserRow[]>('/api/users'),
    enabled,
    ...opts,
  });
}

/**
 * ⚠ `cap` đi XUỐNG MÁY CHỦ, không lọc ở đây. Lọc phía trình duyệt là lọc trong `take` dòng
 *   vừa tải — bấm "cấp 1" ra rỗng trong khi cấp 1 nằm ở dòng thứ 150 (L64).
 */
/*
 * ➕ `v4` `S6` — `cuaToi`: xin ĐÚNG nhật ký của mình. Máy chủ ép `userId`; đây chỉ là lời xin.
 * ⛔ VÀ NÓ PHẢI NẰM TRONG `queryKey`. react-query nhớ theo khoá; hai phạm vi khác nhau dùng
 *   chung một khoá thì bản đã tải của phạm vi này được bày cho phạm vi kia — và ở một trang
 *   nhật ký, "bày nhầm tập" nghĩa là **cho xem việc của người khác từ bộ nhớ đệm**.
 */
export function useAudit(action: string, take = 200, enabled = true, cap = '', cuaToi = false, opts?: Opts<AuditRow[]>) {
  return useQuery({
    queryKey: qk.audit(`${action}|${cap}|${cuaToi ? 'toi' : 'tat-ca'}`),
    queryFn: () => api.get<AuditRow[]>(
      `/api/audit?take=${take}${action ? `&action=${encodeURIComponent(action)}` : ''}`
      + `${cap ? `&cap=${encodeURIComponent(cap)}` : ''}${cuaToi ? '&cuaToi=1' : ''}`
    ),
    enabled,
    ...opts,
  });
}

/** Đếm theo cấp — con số THẬT của cả nhật ký, không phải của `take` dòng đang tải. */
/**
 * TK-25: "Hoạt động gần đây" — lọc theo DANH SÁCH mã ở MÁY CHỦ (`?actions=`), không lọc trong `take` dòng
 *   đã tải: 50 lần "đọc cấu hình" liên tiếp sẽ đẩy mọi lần đăng nhập ra ngoài và ô thành rỗng.
 */
export function useHoatDongCoNghia(ma: readonly string[], take = 6, enabled = true) {
  return useQuery({
    queryKey: qk.audit(`co-nghia|${take}`),
    queryFn: () => api.get<AuditRow[]>(`/api/audit?take=${take}&actions=${ma.join(',')}`),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAuditDem(enabled = true, cuaToi = false) {
  return useQuery({
    /* ⚠ Cùng lý do với `useAudit`: con số đếm phải đi cùng tập nó đếm, kể cả trong bộ nhớ đệm. */
    queryKey: ['audit', 'dem', cuaToi ? 'toi' : 'tat-ca'],
    queryFn: () => api.get<Record<string, number>>(`/api/audit/dem${cuaToi ? '?cuaToi=1' : ''}`),
    enabled,
  });
}

/* --------------------------------------------------- cầu nối realtime */

/*
 * ➕ `GD-10c` 14/09/2026 — `taoGa` DỌN SANG `lib/ga.ts`, và đó không phải một lần dọn cho gọn:
 *   `[114]` phải **CHẠY** cái ga với đồng hồ giả (timer nổ TRỄ) chứ không đọc mặt chữ của nó,
 *   mà bộ kiểm chạy trong Node thì không nạp nổi tệp này (React + react-query). Lý do đầy đủ
 *   nằm ở đầu `lib/ga.ts`; đừng chép nó về đây.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-10b` việc 1 — GA Ở **CẤP MODULE**, và đây là chỗ `GD-10` làm dở.
 *
 * 🔴 dev_device đo sau `GD-10`: nhịp 5 s ĐÚNG, nhưng mỗi lượt ga ra **HAI cặp**
 *   `overview`+`tree` cách nhau 0,2–0,9 s ⇒ 10 lượt/30 s, vẫn quá trần 7.
 * 🔬 TÔI KHÔNG TÁI HIỆN ĐƯỢC bằng bộ đo cũ (luôn ra 6), nên tôi đi tìm bằng THÍ NGHIỆM, không
 *   bằng suy luận. Ba thí nghiệm, mỗi cái đổi đúng một thứ:
 *     · ẩn/hiện tab 3 lần (`refetchOnWindowFocus`) → vẫn 6, cách đều 5,0 s ⇒ **KHÔNG phải**;
 *     · `/me` chậm 600 ms như máy chủ thật        → vẫn 6                ⇒ **KHÔNG phải**;
 *     · gắn `useRealtimeSync()` LẦN THỨ HAI       → **12 lượt, từng cặp** ⇒ ĐÚNG HÌNH DẠNG.
 *   Trong thí nghiệm của tôi hai lượt cách nhau 0,0 s vì hai bộ nghe sinh ra cùng lúc; ở máy
 *   lee chúng cách 0,2–0,9 s vì hai lần gắn xảy ra lệch nhau — cùng một cơ chế, khác thời điểm.
 *
 * ⛔ GỐC LÀ CHỖ GA ĐỨNG, KHÔNG PHẢI SỐ 5000. Ga cũ sinh ra **bên trong `useEffect`**, nên mỗi
 *   bộ nghe có một cái ga RIÊNG — và hai cái ga riêng thì không gộp cho nhau được, dù cả hai
 *   đều "đúng nhịp 5 giây". Hạ 5 s xuống hay lên đều không sửa được điều đó.
 * ⇒ Ga lên CẤP MODULE: một cái cho cả ứng dụng. Từ nay số bộ nghe **không còn nhân số lượt
 *   gọi** — dù ai đó lỡ gắn ba lần, trần vẫn là 7 lượt/30 s.
 * ⚠ VÀ VẪN PHẢI NÓI RA khi bị gắn hai lần: gắn hai lần là một LỖI (mọi khung xử lý hai lượt),
 *   chỉ là từ nay nó không còn đội tải máy chủ. Che nó đi là chữa triệu chứng rồi mất bệnh án —
 *   nên có `console.warn` ở dưới, và nó là thứ sẽ nói ở MÁY LEE, nơi tôi không đo được.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
const gaToanHe = taoGa(5000);
const gaCabinet = new Map<string, ReturnType<typeof taoGa>>();
/**
 * ⚠ `GD-10b` việc 2 — LỊCH SỬ SỐ ĐO 10 s/TỦ, tách hẳn khỏi `meterLatest`.
 *
 * 🔴 `['meter', id]` là truy vấn **800 hàng** (`useMeterHistory`: `take=800`) để vẽ 24 cột giờ.
 *   Một khung số đo mới KHÔNG đổi được hình dáng 24 giờ, nên tải lại nó mỗi 1,2 s là kéo 800
 *   hàng để vẽ lại gần như y hệt.
 * ⛔ NHƯNG `meterLatest` thì **đi thẳng**: đó là con số người trực đang nhìn nhích lên, và nó
 *   là MỘT hàng. Gộp hai thứ này vào một nhịp là hoặc làm chậm cái cần nhanh, hoặc làm nặng
 *   cái không cần.
 */
const gaLichSuDo = new Map<string, ReturnType<typeof taoGa>>();
const gaTheoTu = (kho: Map<string, ReturnType<typeof taoGa>>, id: string, ms: number) => {
  let g = kho.get(id);
  if (!g) { g = taoGa(ms); kho.set(id, g); }
  return g;
};
/** Đếm bộ nghe ĐANG SỐNG — xem khối chú thích ngay trên. */
let soBoNgheSong = 0;

/**
 * Nghe WebSocket và huỷ hiệu lực đúng những query bị ảnh hưởng.
 * Gọi MỘT lần ở khung ứng dụng (Layout), không gọi rải rác trong từng màn hình.
 */
export function useRealtimeSync() {
  const qc = useQueryClient();

  useEffect(() => {
    soBoNgheSong += 1;
    if (soBoNgheSong > 1) {
      /*
       * ⛔ MỘT CÂU, KHÔNG PHẢI MỘT CÁI CHẾT LẶNG. `GD-10b` làm cho lỗi này hết đội tải, nên
       *   nếu không nói ra thì nó sống mãi và không ai biết mọi khung đang được xử lý hai lượt.
       */
      // eslint-disable-next-line no-console
      console.warn(`[realtime] useRealtimeSync đang sống ${soBoNgheSong} bản — phải đúng 1. `
        + 'Mỗi khung đang được xử lý nhiều lượt; tìm chỗ gắn thừa (đúng chỗ là Layout).');
    }

    /**
     * `overview` + `tree` — MỌI loại khung đi qua đây, không loại nào được đi cửa riêng.
     * ⚠ Ga nằm ngoài `useEffect` (cấp module) nên hai bộ nghe DÙNG CHUNG nó — đó chính là
     *   điều `GD-10b` mua về.
     */
    const lamMoiToanHe = () => gaToanHe.goi(() => {
      qc.invalidateQueries({ queryKey: qk.overview });
      qc.invalidateQueries({ queryKey: qk.tree });
    });
    const gaCuaTu = (id: string) => gaTheoTu(gaCabinet, id, 1000);

    const off = realtime.onMessage((msg: RtMessage) => {
      const cabinetId = typeof (msg as any).cabinetId === 'string' ? (msg as any).cabinetId : null;

      switch (msg.type) {
        case 'cabinet.state':
          /*
           * 🚨 KHUNG NÀY MANG MỘT THỨ KHÔNG CÓ ĐƯỜNG NÀO KHÁC ĐỂ TỚI — `nguonGiuQuyen`.
           *   Mọi trường khác ở đây đều là tiếng vọng của CSDL, nên `invalidateQueries` một
           *   mình là đủ, và suốt từ đầu dự án nó đúng. Trường này KHÁC: CSDL **không có
           *   cột ấy** ⇒ vứt khung đi là nhãn *"Đang giữ bởi: …"* không bao giờ gọi được tên.
           * ⇒ Rót vào kho sống TRƯỚC khi huỷ hiệu lực, để lần vẽ lại kế tiếp đọc được ngay.
           */
          /*
           * ⛔ RÓT KHO SỐNG **KHÔNG QUA GA** — và đây là ranh giới quan trọng nhất của `GD-10`:
           *   ga chỉ hoãn việc ĐI HỎI LẠI MÁY CHỦ, không bao giờ hoãn dữ liệu TỦ VỪA NÓI.
           *   `nguonGiuQuyen` không có cột trong CSDL; hoãn nó là mất nó. Nhãn *"Đang giữ
           *   bởi: …"* vẫn đổi tức thì như trước, đúng luật `useNguonGiuSong`.
           */
          if (cabinetId) nhanKhungNguonGiu(cabinetId, (msg as any).outputs,
            (msg as any).khungNoiVeNguonGiu === true);
          /* ➕ `GD-10` — trang tủ đang mở: tối đa 1 lượt/1 s/TỦ. */
          if (cabinetId) gaCuaTu(cabinetId).goi(
            () => qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) }));
          lamMoiToanHe();
          break;

        case 'cabinet.online':
        case 'cabinet.offline':
          /*
           * ═══════════════════════════════════════════════════════════════════════════════
           * 🔴 YC-49 — LẬT 09/09. Bản trước XOÁ tên nguồn ở đây; nay KHÔNG xoá nữa.
           *
           * Tôi viết: *"một cái tên cũ tệ hơn 'chưa biết'"*. Câu ấy đúng cho một cái tên đã
           * HẾT HẠN — nhưng socket rớt **không phải** một lời khai rằng quyền giữ đã đổi.
           * Luật dev_device chốt: **trạng thái giữ quyền chỉ đổi khi TỦ NÓI nó đổi. Chỉ khởi
           * động lại mới xoá quyền giữ.**
           *
           * ⛔ Và đó chính là luật tôi vừa tự đặt ở lượt 34, áp cho chính mình lần này:
           *   **người phát khai, KHÔNG suy từ sự vắng mặt.** Một đường truyền đứt là một sự
           *   VẮNG MẶT. Suy "không ai giữ" từ nó là đúng cái bẫy tôi vừa bịt ở khung v1 —
           *   chỉ khác chỗ đứng. Mỗi lần rớt sóng là một lần nhãn nhấp nháy về "chưa rõ".
           *
           * ⚠ dev_device gọi tên chỗ này là *"bẫy dễ mắc: hầu hết app đều dọn sạch trạng thái
           *   khi mở lại kết nối"* — và tôi đã mắc đúng nó, ở đúng lượt mình đang bịt nó.
           *
           * 📌 RỦI RO CÒN LẠI, ghi ra chứ không giấu: nếu tủ KHỞI ĐỘNG LẠI trong lúc mất kết
           *   nối, quyền giữ đã bị xoá phía tủ mà app vẫn giữ tên cũ — cho tới khung
           *   `CabinetState` đầu tiên (đo được: khung ấy về ngay trong cùng giây nối lại,
           *   09:18:37). Cửa sổ ấy đóng HẲN khi `lyDoKhoiDong` (P-3 mục B) lên tủ: lúc đó app
           *   phân biệt được "nối lại" với "vừa khởi động lại". Đó là lý do trường ấy tồn tại.
           * ═══════════════════════════════════════════════════════════════════════════════
           */
          /*
           * ⚠ ONLINE/OFFLINE đi thẳng, KHÔNG qua ga tủ: nó hiếm (một tủ lên/xuống mạng), và nó
           *   là thứ người trực nhìn để quyết định gửi lệnh hay không. Ga sinh ra cho dòng
           *   khung DỒN DẬP; dùng nó ở chỗ thưa chỉ thêm độ trễ mà không mua được gì.
           */
          if (cabinetId) qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
          lamMoiToanHe();
          break;

        /*
         * ═══════════════════════════════════════════════════════════════════════════════
         * 🔴 `GD-10` — ĐÂY LÀ CÁI VÒI. Demo phát mỗi 1,2 s; bản cũ huỷ BỐN thứ mỗi khung, và
         *   một trong bốn là `overview` — một truy vấn TỔNG HỢP toàn hệ, chạy lại vì MỘT tủ
         *   vừa báo điện áp.
         * ⛔ Số đo mới thì **không gộp**: `meterLatest` và `meter` là đúng thứ khung này mang
         *   theo, và là đúng thứ người trực đang nhìn nhích lên. Hoãn chúng là làm hỏng cái
         *   duy nhất khung này có ích.
         * ⛔ `qk.cabinet(id)` RA KHỎI ĐÂY (phiếu chốt): khung số đo KHÔNG đổi hàng `Cabinet` —
         *   tên, tuyến, trạng thái ngõ ra đều do `cabinet.state`/`online` nói. Huỷ nó ở đây là
         *   tải lại cả bản ghi tủ 1,2 s một lần để đọc lại đúng những trường không đổi.
         * ⚠ `['meter', cabinetId]` là TIỀN TỐ có chủ ý — `qk.meter(id, hours)` có ba mảnh, và
         *   một trang có thể mở nhiều khung giờ. Tiền tố bắt hết, không phải một chỗ quên.
         * ═══════════════════════════════════════════════════════════════════════════════
         */
        case 'cabinet.meter':
          if (cabinetId) {
            /*
             * TK-16 B4 — số trên CHÍNH khung này vào thẳng bộ đệm. Từ B1/B3 máy chủ ghi CSDL theo lô
             *   mỗi 1 s, nên gọi lại API ngay lúc khung về sẽ đọc phải HÀNG CŨ. Bảng trực tiếp đọc
             *   `meterTrucTiep`, không đọc CSDL.
             */
            const km = msg as Extract<RtMessage, { type: 'cabinet.meter' }>;
            qc.setQueryData(qk.meterTrucTiep(cabinetId), {
              meter: km.meter, at: km.at, lyDo: km.lyDo ?? null,
            } satisfies SoDoTrucTiep);
            /*
             * ➕ `GD-10b` việc 2 — HAI NHỊP CHO HAI THỨ KHÁC HẲN NHAU:
             *   · `meterLatest` — MỘT hàng, là con số người trực đang nhìn ⇒ đi THẲNG;
             *   · `['meter', id]` — 800 hàng dựng 24 cột giờ ⇒ ga 10 s/TỦ. Một mẫu mới không
             *     đổi được hình 24 giờ, nên kéo 800 hàng mỗi 1,2 s là trả giá cho một bức
             *     tranh gần như y hệt.
             */
            qc.invalidateQueries({ queryKey: qk.meterLatest(cabinetId) });
            gaTheoTu(gaLichSuDo, cabinetId, 10_000).goi(
              () => qc.invalidateQueries({ queryKey: ['meter', cabinetId] }));
          }
          lamMoiToanHe();
          break;

        case 'cabinet.alarm':
          /*
           * 🔴 PHÁT SỰ KIỆN, không chỉ làm mới bộ đệm. Làm mới bộ đệm khiến một con số nhích
           *   lên trong một danh sách; nó KHÔNG đưa được gì tới mắt người đang trực, mà mức 1
           *   thì đúng là phải tới mắt (YC-29 PHẦN 2, quy tắc 1). Cùng khuôn `command.ack`.
           * ⚠ Lọc mức là việc của `lib/popup.ts`, không phải của chỗ này — một nhánh `if`
           *   thứ hai về mức ở đây là cửa thứ hai cho cùng một luật.
           */
          window.dispatchEvent(new CustomEvent(EVENT_CANH_BAO_MOI, { detail: msg }));
          if (cabinetId) {
            qc.invalidateQueries({ queryKey: qk.alarms(cabinetId) });
            qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
          }
          qc.invalidateQueries({ queryKey: qk.alarms() });
          /* ⚠ Danh sách cảnh báo đi THẲNG (người trực đang chờ nó); chỉ `overview`/`tree` qua ga. */
          lamMoiToanHe();
          break;

        /*
         * ═══════════════════════════════════════════════════════════════════════════════
         * 🔴 CHIỀU ĐÓNG — trước hôm nay loại tin này CHƯA TỪNG CÓ NGƯỜI NGHE (YC-47).
         *
         * Máy chủ phát nó từ lâu; ở đây chỉ có khai báo kiểu, không `case` nào. ⇒ Mở thì
         * ĐẨY tới mắt người trực, đóng thì chỉ có `useAlarms` tự làm mới sau 60 giây — và
         * **popup thì không có đường đóng nào cả**, nó chỉ tắt khi có người bấm.
         *
         * ⛔ Mà `cauChanPopup()` in nguyên văn cho họ: *"Cảnh báo này TỰ TẮT khi tủ báo đã
         *   hết."* App nói một hành động nó không làm — đúng hạng YC-47.
         * ⇒ Mở tới mắt người thì đóng cũng phải tới mắt người. Cùng khuôn `cabinet.alarm`.
         * ═══════════════════════════════════════════════════════════════════════════════
         */
        case 'cabinet.alarm.cleared':
          window.dispatchEvent(new CustomEvent(EVENT_CANH_BAO_DONG, { detail: msg }));
          if (cabinetId) {
            qc.invalidateQueries({ queryKey: qk.alarms(cabinetId) });
            qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
          }
          qc.invalidateQueries({ queryKey: qk.alarms() });
          lamMoiToanHe();
          break;

        case 'cabinet.schedule':
          if (cabinetId) {
            qc.invalidateQueries({ queryKey: qk.schedule(cabinetId) });
            qc.invalidateQueries({ queryKey: qk.scheduleHistory(cabinetId) });
            qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
          }
          break;

        case 'cabinet.log':
          if (cabinetId) qc.invalidateQueries({ queryKey: qk.logs(cabinetId) });
          break;

        case 'command.ack':
        case 'command.timeout':
          // Bản tin này KHÔNG kèm cabinetId (xem deviceGateway.ts) → làm mới mọi
          // danh sách lệnh đang mở, số lượng luôn nhỏ nên không tốn kém.
          qc.invalidateQueries({ queryKey: ['commands'] });
          /* ➕ `GD-10` — `tree` là số liệu toàn hệ ⇒ qua ga, y như mọi loại khung khác. */
          lamMoiToanHe();
          /*
           * 🔴 VÀ PHÁT RA MỘT SỰ KIỆN — làm mới bộ đệm là chưa đủ.
           *
           * dev_device vừa cho firmware trả `detail` mang GIÁ TRỊ bị từ chối
           * (*"ngõ 2 ô 12: ngày đặc biệt 0/5"*). Nếu chỗ này chỉ `invalidate`, câu ấy về tới
           * CSDL và nằm trong một bảng nhật ký ở TRANG KHÁC — còn người vừa bấm nút thì
           * thấy một dòng xanh "Đã đẩy lịch xuống tủ" rồi thôi.
           *
           * ⛔ Đúng hình dạng L60 mà anh vừa tự nhận, lần này ở phía tôi: tôi dựng đường
           *    `detail` cho nhánh ACK-có-mã, rồi không đưa nó tới chỗ người ta ĐANG NHÌN.
           */
          window.dispatchEvent(new CustomEvent(EVENT_LENH_ACK, { detail: msg }));
          break;

        default:
          break;
      }
    });
    /*
     * ⛔ GỠ BỘ NGHE **VÀ** DỌN MỌI HẸN CÒN TREO. Thiếu vế sau thì một `setTimeout` của ga sẽ
     *   nổ sau khi phiên đã đóng — vô hại hôm nay, và là một sợi dây không ai đo.
     * ⚠ `GD-10b` — GA NAY DÙNG CHUNG, nên **chỉ dọn khi bộ nghe CUỐI CÙNG ra đi**. Dọn sớm là
     *   một bộ nghe đang sống bị bẻ mất cái hẹn của nó — đúng lỗi ngược lại với lỗi vừa sửa.
     */
    return () => {
      off();
      soBoNgheSong -= 1;
      if (soBoNgheSong > 0) return;
      gaToanHe.don();
      for (const g of gaCabinet.values()) g.don();
      gaCabinet.clear();
      for (const g of gaLichSuDo.values()) g.don();
      gaLichSuDo.clear();
    };
  }, [qc]);
}

/**
 * Đọc `nguonGiuQuyen` SỐNG của một tủ.
 *
 * 🔴 CÂU HỎI dev_device đặt: *"nếu app chỉ vẽ lại khi `on` đổi thì nhãn sẽ chết"*. Chỗ này là
 *   câu trả lời, và nó phải đúng cho ca KHÓ: **màn hình chiếm quyền mà KHÔNG đổi rơ-le**.
 *   `useSyncExternalStore` bắt vào SỐ PHIÊN BẢN của kho, nên mỗi khung `cabinet.state` đều
 *   buộc vẽ lại — **không cần `on` đổi**, và cũng không đợi `invalidateQueries` trả về dữ liệu
 *   khác (react-query dùng structural sharing: dữ liệu REST y hệt ⇒ **không** vẽ lại).
 *
 * ⚠ Ảnh chụp là một SỐ, không phải cái Map — trả Map mới mỗi lần gọi là vòng lặp vô hạn.
 */
export function useNguonGiuSong(cabinetId: string | undefined): (index: number) => number | undefined {
  const phienBan = useSyncExternalStore(ngheNguonGiuSong, phienBanNguonGiuSong, () => 0);
  return useMemo(
    () => (index: number) => (cabinetId ? docNguonGiuSong(cabinetId, index) : undefined),
    [cabinetId, phienBan],
  );
}

/** Trạng thái kết nối realtime để hiện chấm báo ở thanh trên. */
export function useRealtimeStatus(): RtStatus {
  return useSyncExternalStore(
    (cb) => realtime.onStatus(cb),
    () => realtime.getStatus(),
    () => 'idle' as RtStatus
  );
}

/* ---------------------------------------------------------------- TK-16 B4 */

/** Một khung `cabinet.meter` vừa về: số đo + lúc về + lý do gửi (null = firmware cũ không khai). */
export interface SoDoTrucTiep { meter: Record<string, number>; at: number; lyDo: 'DINH_KY' | 'SU_KIEN' | null }

/**
 * Số đo trực tiếp của MỘT tủ — chỉ đọc bộ đệm do `useRealtimeSync` rót vào. `enabled: false`:
 * KHÔNG có queryFn nào chạy, nên không bao giờ đọc phải hàng CSDL cũ hơn khung.
 */
export function useSoDoTrucTiep(id: string | undefined) {
  return useQuery<SoDoTrucTiep | null>({
    queryKey: qk.meterTrucTiep(id ?? ''),
    queryFn: async () => null,
    enabled: false,
    staleTime: Infinity,
  });
}

/** `/health` của máy chủ (công khai) — cho thẻ "Sức khoẻ máy chủ" ở Quản trị. */
export interface SucKhoeMayChu {
  ok: boolean; uptimeSec: number; devicesOnline: number; tuDangGui?: number; appClients?: number;
  meterHangDoi?: number; meterBoRoi?: number; meterDaNhan?: number; meterGhiHong?: number; meterLoDaGhi?: number;
}

/** TK-19 — tủ nhóm theo bản firmware. Chỉ `SUPER_ADMIN` (máy chủ chặn hạng khác). */
export function useThongKeFirmware(enabled = true) {
  return useQuery({ queryKey: ['firmware-thong-ke'], queryFn: () => api.get<ThongKeFirmware>('/api/firmware/thong-ke'),
                   enabled, refetchInterval: 60_000 });
}

export function useSucKhoeMayChu() {
  return useQuery<SucKhoeMayChu>({
    queryKey: ['suc-khoe-may-chu'],
    queryFn: async () => {
      const r = await fetch(`${(import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')}/health`);
      if (!r.ok) throw new Error(`/health trả ${r.status}`);
      return (await r.json()) as SucKhoeMayChu;
    },
    refetchInterval: 30_000,
  });
}
